import { differenceInDays } from 'date-fns';
import { CordovaPlugins } from '../../shared/CordovaPlugins';
import { UserDevice } from '../shared/forms';
import { isAndroid, isWeb, isNative, isIos } from '../../shared/utils/is';
import { LocalNotifications } from '@capacitor/local-notifications';
import { PushNotifications } from '@capacitor/push-notifications';

const DEVICE_ID_KEY = 'push_notifications_device_id';
const LAST_PUSH_ID_KEY = 'last-push-id';
const LAST_PUSH_TIMESTAMP_KEY = 'last_push_notification_registration';
const ON_PUSHWOOSH_RECEIVED_EVENT = 'push-receive';
const ON_PUSHWOOSH_TAPPED_EVENT = 'push-notification';

// @todo getSetting / setSetting are temporary until we refactor the entire $f.mobile.settings to use promises
const getSetting = async (key) => {
    return new Promise((resolve, reject) => {
        $f.mobile.settings.get(key, resolve, reject);
    });
};

const setSetting = async (key, value) => {
    return new Promise((resolve, reject) => {
        $f.mobile.settings.set(key, value, resolve, reject);
    });
};

const getDeviceId = async () => {
    return await getSetting(DEVICE_ID_KEY);
};

const setDeviceId = async (id) => {
    await setSetting(DEVICE_ID_KEY, id);
    await setSetting(LAST_PUSH_TIMESTAMP_KEY, new Date());
};

const getLastRegistration = async () => {
    const dateString = await getSetting(LAST_PUSH_TIMESTAMP_KEY);

    if (!dateString) {
        return null;
    }

    return new Date(dateString);
};

export const getPushDataFromEvent = (event) => event?.notification?.userdata || {};

/** @jira:ZP-3379 @jira:ZP-3376 iOS sends multiple events for the same push notification. We'll block
 * duplicate notifications by checkign for a duplicate timestamp. We can't track this in memory because we
 * sometimes refresh the page when redirecting.
 */
const isDuplicatePush = async (pushId) => {
    const lastId = localStorage.getItem(LAST_PUSH_ID_KEY);
    return lastId === pushId;
};

export const handlePushTapped = async (event) => {
    const pushData = getPushDataFromEvent(event);
    const url = pushData.url;
    const pushId = pushData.push_id;

    if (!url) {
        return;
    }

    if (!isIos() || !pushId) {
        $f.mobile.redirect(url);
        return;
    }

    const isDupe = await isDuplicatePush(pushId);

    if (isDupe) {
        return;
    }

    localStorage.setItem(LAST_PUSH_ID_KEY, pushId);

    $f.mobile.redirect(url);
};

export const handlePushReceived = (event) => {
    const pushData = getPushDataFromEvent(event);
    const url = pushData.url;

    if (!url) {
        return;
    }

    // if notification is related to comments, update the unread count
    if (url.includes('comments')) {
        $f.mobile.unread_notifications_count.fetch();
    }
};

const isEligibleForRegistration = async () => {
    if (!$f.active_user.is_logged_in()) {
        return false;
    }

    const lastRegistration = await getLastRegistration();

    if (!lastRegistration) {
        return true;
    }

    return differenceInDays(new Date(), lastRegistration) >= 1;
};

const registerWithPushwoosh = () =>
    new Promise((resolve) => {
        CordovaPlugins().pushwoosh.registerDevice(
            () => {
                resolve();
            },
            () => {
                console.error('Failed to register for push notifications with Pushwoosh');
                resolve();
            }
        );
    });

const registerWithZenputOld = async () => {
    const pushToken = await new Promise(CordovaPlugins().pushwoosh.getPushToken);
    const device = new UserDevice({
        id: pushToken,
        name: $f.mobile.settings.get_platform_type('Unknown', true)
    });
    return new Promise((resolve) => {
        device.save(
            {},
            {
                success: async () => {
                    await setDeviceId(pushToken);
                    resolve();
                },
                error: () => {
                    // @author:david nothing we can do - device registration will retry the next time the app is opened
                    resolve();
                }
            }
        );
    });
};

const registerWithZenput = async (token) => {
    const device = new UserDevice({
        id: token,
        name: $f.mobile.settings.get_platform_type('Unknown', true)
    });
    return new Promise((resolve) => {
        device.save(
            {},
            {
                success: async () => {
                    await setDeviceId(token);
                    resolve();
                },
                error: () => {
                    // @author:david nothing we can do - device registration will retry the next time the app is opened
                    resolve();
                }
            }
        );
    });
};

export const addCapacitorPushNotificationsListeners = async () => {
    await PushNotifications.addListener('registration', async (token) => {
        await registerWithZenput(token.value);
    });

    await PushNotifications.addListener('registrationError', (err) => {
        $f.mobile.logging.log_error('Error registering device', { error: err });
    });

    await PushNotifications.addListener('pushNotificationReceived', async (event) => {
        const url = JSON.parse(event.data.u).url;

        // if notification is related to comments, update the unread count
        if (!!url && url.includes('comments')) {
            $f.mobile.unread_notifications_count.fetch();
        }
    });

    await PushNotifications.addListener('pushNotificationActionPerformed', async (event) => {
        const pushData = JSON.parse(event.notification.data.u);
        const url = pushData.url;
        const pushId = pushData.push_id;

        if (!url) {
            return;
        }

        if (!isIos() || !pushId) {
            $f.mobile.redirect(url);
            return;
        }

        const isDupe = await isDuplicatePush(pushId);
        if (isDupe) {
            return;
        }
        localStorage.setItem(LAST_PUSH_ID_KEY, pushId);

        $f.mobile.redirect(url);
    });
};

export const registerWithCapacitor = async () => {
    try {
        const permStatus = await PushNotifications.requestPermissions();
        if (permStatus.receive === 'granted') {
            await PushNotifications.register();
        }
        await LocalNotifications.requestPermissions();
    } catch (error) {
        $f.mobile.logging.log_error('Error with device permissions', { error: error });
    }

    if (isAndroid()) {
        await PushNotifications.createChannel({
            id: 'push_notifications',
            name: 'Push Notifications',
            importance: 4
        });
    }
    await addCapacitorPushNotificationsListeners();
};

export const unregisterDevice = async () => {
    const deviceId = await getDeviceId();
    const device = new UserDevice({ id: deviceId });

    device.destroy(); // hit our server and remove

    await setSetting(DEVICE_ID_KEY, null);
    await setSetting(LAST_PUSH_TIMESTAMP_KEY, null);

    if (isNative()) {
        CordovaPlugins().pushwoosh.unregisterDevice();
    }
};

/**
 * This will initialize our device to receive push notifications. Once we receive
 * a device token (id) from our provider, we send it to our servers for storage.
 */
export const initializePushNotifications = async () => {
    if (isWeb()) {
        return;
    }

    if (!$f.active_user.is_logged_in()) {
        return;
    }

    if ($f.flags.get_flag('use_local_notifications')) {
        await registerWithCapacitor();
        await LocalNotifications.addListener(
            'localNotificationActionPerformed',
            $f.mobile.notifications.onLocalNotificationActionPerformed
        );
    } else {
        // Fired when the user taps on the notification (even when the app is
        // force-closed; the app will resume and then fire this).
        document.removeEventListener(ON_PUSHWOOSH_TAPPED_EVENT, handlePushTapped);
        document.addEventListener(ON_PUSHWOOSH_TAPPED_EVENT, handlePushTapped);

        // Fired when the device recieves a notification
        document.removeEventListener(ON_PUSHWOOSH_RECEIVED_EVENT, handlePushReceived);
        document.addEventListener(ON_PUSHWOOSH_RECEIVED_EVENT, handlePushReceived);

        // @author:david A note about Windows:
        // Unlike iOS/Android, Windows push notifications will only work with apps signed for the Windows Store.
        // This means that even for development builds, in order to test pushes, the app needs to be signed with
        // our Windows Store certificate (or "associated" with the app store in Visual Studio). Pushes to production
        // will still only target production builds, however, since we specify a different appid (process.env.PUSHWOOSH_APP_ID) below.

        // Initialize Pushwoosh. This will trigger all pending push notifications on start.
        CordovaPlugins().pushwoosh.onDeviceReady({
            appname: 'Zenput',
            appid: process.env.PUSHWOOSH_APP_ID,
            projectid: process.env.FCM_SENDER_ID
        });

        if (isAndroid()) {
            // Without this, new pushes will remove previous ones
            CordovaPlugins().pushwoosh.setMultiNotificationMode(
                () => {},
                (err) => {
                    $f.mobile.logging.log_error('Failed to setMultiNotificationMode for Pushwoosh', err);
                }
            );
        }

        const shouldRegister = await isEligibleForRegistration();

        if (shouldRegister) {
            await registerWithPushwoosh();
            await registerWithZenputOld();
        }
    }
};
